import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import AppShell from "@/components/AppShell";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { LayoutDashboard, Users, Mail, MessageSquare, ListChecks, Megaphone, BarChart3, Settings as SettingsIcon } from "lucide-react";
import AdminDashboardTab from "@/components/admin/AdminDashboardTab";
import AdminSubscribersTab from "@/components/admin/AdminSubscribersTab";
import AdminEmailsTab from "@/components/admin/AdminEmailsTab";
import AdminFeedbackTab from "@/components/admin/AdminFeedbackTab";
import AdminChecklistTab from "@/components/admin/AdminChecklistTab";
import AdminChangelogTab from "@/components/admin/AdminChangelogTab";
import AdminAnalyticsTab from "@/components/admin/AdminAnalyticsTab";

const TABS = [
  { key: "dashboard", label: "Dashboard", icon: LayoutDashboard, Comp: AdminDashboardTab },
  { key: "analytics", label: "Analytics", icon: BarChart3, Comp: AdminAnalyticsTab },
  { key: "subscribers", label: "Subscribers", icon: Users, Comp: AdminSubscribersTab },
  { key: "emails", label: "Emails & SMS", icon: Mail, Comp: AdminEmailsTab },
  { key: "feedback", label: "Feedback", icon: MessageSquare, Comp: AdminFeedbackTab },
  { key: "checklist", label: "Roadmap", icon: ListChecks, Comp: AdminChecklistTab },
  { key: "changelog", label: "Changelog", icon: Megaphone, Comp: AdminChangelogTab },
];

export default function AdminLayout() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [activeTab, setActiveTab] = useState(() => {
    try { return localStorage.getItem("hc_admin_tab") || "dashboard"; } catch (e) { return "dashboard"; }
  });
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);

  useEffect(() => {
    api.get("/admin/me")
      .then((r) => { if (!r.data.is_admin) setForbidden(true); })
      .catch(() => setForbidden(true))
      .finally(() => setLoading(false));
  }, []);

  const switchTab = (key) => {
    setActiveTab(key);
    try { localStorage.setItem("hc_admin_tab", key); } catch (e) { /* noop */ }
  };

  if (loading) return <AppShell><div className="p-10 text-[#7A736E]">Loading…</div></AppShell>;
  if (forbidden) return (
    <AppShell>
      <div className="max-w-xl mx-auto p-12 text-center">
        <h1 className="font-display text-3xl mb-3">Admin only</h1>
        <p className="text-[#7A736E] mb-6">This area is just for the Hope Chest team.</p>
        <Button onClick={() => nav("/dashboard")} className="rounded-full bg-[#3A5A4A] hover:bg-[#2C4538]">Back to dashboard</Button>
      </div>
    </AppShell>
  );

  const ActiveComp = (TABS.find((t) => t.key === activeTab) || TABS[0]).Comp;

  return (
    <AppShell>
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        <div className="mb-6">
          <p className="text-xs uppercase tracking-[0.2em] text-[#7A736E]">Admin · Back office</p>
          <h1 className="font-display text-3xl md:text-4xl font-semibold mt-1">Hope Chest control center</h1>
          <p className="text-[#7A736E] mt-1 text-sm">Signed in as {user?.email}</p>
        </div>

        <div className="flex flex-col lg:flex-row gap-6">
          {/* Side tabs */}
          <aside className="lg:w-56 flex-shrink-0">
            <nav className="flex lg:flex-col gap-1 overflow-x-auto lg:overflow-visible pb-2 lg:pb-0" data-testid="admin-tab-nav">
              {TABS.map((t) => {
                const active = t.key === activeTab;
                return (
                  <button
                    key={t.key}
                    onClick={() => switchTab(t.key)}
                    className={`flex items-center gap-2.5 px-4 py-2.5 rounded-xl text-sm font-medium transition whitespace-nowrap text-left ${
                      active ? "bg-[#3A5A4A] text-white shadow-sm" : "text-[#7A736E] hover:bg-[#F3EFEA] hover:text-[#3D3935]"
                    }`}
                    data-testid={`admin-tab-${t.key}`}
                  >
                    <t.icon className="w-4 h-4 flex-shrink-0" />
                    {t.label}
                  </button>
                );
              })}
            </nav>
          </aside>

          {/* Content */}
          <main className="flex-1 min-w-0">
            <ActiveComp />
          </main>
        </div>
      </div>
    </AppShell>
  );
}
